import React from "react";
import { Navigate, useParams } from "react-router-dom";
import { getRouteKeyFromPath, SUPPORTED_LANGUAGES } from "@/lib/i18nRoutes";
import MissingRoute from "@/lib/MissingRoute.jsx";

const Dashboard = React.lazy(() => import("./Dashboard"));
const NavigationPage = React.lazy(() => import("./Navigation"));
const Settings = React.lazy(() => import("./Settings"));
const SkipperTools = React.lazy(() => import("./SkipperTools"));
const Advertise = React.lazy(() => import("./Advertise"));
const SearchPage = React.lazy(() => import("./public/SearchPage"));
const Safety = React.lazy(() => import("./Safety"));
const PublicIndexPage = React.lazy(() => import("./public/PublicIndexPage"));
const CustomMarstickInfo = React.lazy(() => import("./CustomMarstickInfo"));
const Feedback = React.lazy(() => import("./Feedback"));
const AvailableSoonPage = React.lazy(() => import("./public/AvailableSoonPage"));
const OfflineNavigation = React.lazy(() => import("./OfflineNavigation"));
const DailyBriefingPage = React.lazy(() => import("./public/DailyBriefingPage"));
const TrustPolicyPage = React.lazy(() => import("./public/TrustPolicyPage"));
const Contact = React.lazy(() => import("./Contact"));
const Privacy = React.lazy(() => import("./Privacy"));
const TermsAndConditions = React.lazy(() => import("./TermsAndConditions"));
const CookiePolicy = React.lazy(() => import("./CookiePolicy"));
const RoutesHub = React.lazy(() => import("./RoutesHub"));
const RegulationsHub = React.lazy(() => import("./RegulationsHub"));
const GlossaryHub = React.lazy(() => import("./GlossaryHub"));
const PortEntryPack = React.lazy(() => import("./PortEntryPack"));
const AISCollisionCenter = React.lazy(() => import("./AISCollisionCenter"));
const BoatDocumentVault = React.lazy(() => import("./BoatDocumentVault"));
const MarstickBusinessTier = React.lazy(() => import("./MarstickBusinessTier"));
const FleetOperations = React.lazy(() => import("./FleetOperations"));
const Logbook = React.lazy(() => import("./Logbook"));

// ── Slug → component map for /:lang/:slug routes ──────────────────────────────
// IMPORTANT: every route key listed in `localizedRoutes` / `pageSeo` that
// reaches this component via /:lang/:slug MUST have an entry here.
// Direct routes under /:lang/ (training-center, skipper-tools, advertise) bypass
// this component and don't need entries.
//
// Missing entries cause LocalizedPublicPage to redirect to /:lang/ — which is
// what was breaking /es/feedback, /he/premium, etc. and surfacing them in
// Google Search Console under "Page with redirect".
const PublicNavigationSection = () => <NavigationPage />;
const PublicWeatherSection = () => <PublicIndexPage pageKey="weather" />;
const PublicWeatherForecastSection = () => <PublicIndexPage pageKey="weatherForecast" />;
const PublicSafetySection = () => <Safety />;
const PublicMarinasSection = () => <PublicIndexPage pageKey="marinas" />;
const PublicRadarSection = () => <PublicIndexPage pageKey="radar" />;
const PublicLearningSection = () => <PublicIndexPage pageKey="learn" />;
const PublicDestinationsSection = () => <PublicIndexPage pageKey="destinations" />;
const PublicToolsSection = () => <PublicIndexPage pageKey="tools" />;
const PublicWeatherAliasSection = () => <PublicIndexPage pageKey="weather" />;
const AboutSection = () => <TrustPolicyPage policySlug="about" />;
const AdvertisingPolicySection = () => <TrustPolicyPage policySlug="advertising-policy" />;
const EditorialPolicySection = () => <TrustPolicyPage policySlug="editorial-policy" />;
const DisclaimerSection = () => <TrustPolicyPage policySlug="disclaimer" />;
const SafetyDisclaimerSection = () => <TrustPolicyPage policySlug="safety-disclaimer" />;

const pageComponents = {
  navigation:     PublicNavigationSection,
  weather:        PublicWeatherSection,
  weatherForecast: PublicWeatherForecastSection,
  safety:         PublicSafetySection,
  marinas:        PublicMarinasSection,
  radar:          PublicRadarSection,
  learningCenter: PublicLearningSection,
  learn:          PublicLearningSection,
  trainingCenter: PublicLearningSection,
  destinations:   PublicDestinationsSection,
  tools:          PublicToolsSection,
  marineWeather:  PublicWeatherAliasSection,
  weatherForecastForSailing: PublicWeatherAliasSection,
  windForecastForBoating: PublicWeatherAliasSection,
  seaWeatherForecast: PublicWeatherAliasSection,
  custom:         CustomMarstickInfo,
  dashboard:      Dashboard,
  feedback:       Feedback,
  premium:        AvailableSoonPage,
  offline:        OfflineNavigation,
  settings:       Settings,
  skipperTools:   SkipperTools,
  advertise:      Advertise,
  search:         SearchPage,
  logbook:        Logbook,
  dailyBriefing:  DailyBriefingPage,
  about:          AboutSection,
  contact:        Contact,
  privacy:        Privacy,
  terms:          TermsAndConditions,
  cookies:        CookiePolicy,
  advertisingPolicy: AdvertisingPolicySection,
  editorialPolicy: EditorialPolicySection,
  disclaimer:     DisclaimerSection,
  safetyDisclaimer: SafetyDisclaimerSection,
  routes:         RoutesHub,
  regulations:    RegulationsHub,
  glossary:       GlossaryHub,
  portEntry:      PortEntryPack,
  aisCollisionCenter: AISCollisionCenter,
  boatDocumentVault: BoatDocumentVault,
  businessTier:   MarstickBusinessTier,
  fleetOperations: FleetOperations,
};

export default function LocalizedPublicPage() {
  const { lang, slug } = useParams();

  if (!SUPPORTED_LANGUAGES.includes(lang)) {
    return <Navigate to="/en/" replace />;
  }

  const routeKey      = getRouteKeyFromPath(`/${lang}/${slug || ""}`);
  const PageComponent = pageComponents[routeKey];

  if (!slug) {
    return <Dashboard />;
  }

  if (!PageComponent) return <MissingRoute />;

  return (
    <>
      <PageComponent />
    </>
  );
}